import { CoreLayoutView, CoreTextView, ORIENTATION_ENUM } from '@core/widgets';
import { useBehavior, useButton, useTriggerAction } from '@libs/core';
import { ActionActionTypeEnum, TWidgetProps } from '@libs/types';
import React from 'react';
import { getSemanticColors, SemanticColor } from '../../theme/colors';
import { TInfoBannerData } from '../../types/data';
import { TInfoBannerOptions } from '../../types/options';
import { VisualView } from '../Image/VisualView';

export const InfoBanner = ({ widget }: TWidgetProps<TInfoBannerData, TInfoBannerOptions>) => {
    const semanticColors = getSemanticColors(widget.version);
    const { isHidden, isDisabled } = useBehavior(widget);
    const { triggerActionsByType, triggerActionsByNameAndType } = useTriggerAction(widget);
    const { handleClick } = useButton(widget, { isDisabled, triggerActionsByType, triggerActionsByNameAndType });
    const messages = widget.data?.messages ?? {};
    const options = widget.options ?? {};
    const parentStyleNames = [options.style ?? ' '].filter(Boolean) as string[];
    const hasOnClickAction = widget.actions?.some((a) => a?.actionType === ActionActionTypeEnum.onClick) ?? false;

    return (
        <CoreLayoutView
            widget={widget}
            uid={widget.uid}
            isHide={isHidden}
            isDisabled={isDisabled}
            parentStyleNames={parentStyleNames}
            styleName="infoBannerHorizontalLayout"
            onClick={hasOnClickAction ? () => handleClick() : undefined}
            styleProps={{
                orientation: ORIENTATION_ENUM.HORIZONTAL,
                lineY: 'center',
                padding: { top: 16, right: 16, bottom: 16, left: 16 },
                gap: 16,
                margin: options.margin,
                backgroundColor: semanticColors[SemanticColor.FillSecondary],
                borderRadius: 12,
                borderStroke: 1,
                borderColor: semanticColors[SemanticColor.BorderEmphasised],
                boxShadow: 'var(--shadow-float-secondary)',
                overflow: 'hidden',
                hovered: {
                    backgroundColor: semanticColors[SemanticColor.FillBannerHovered],
                },
                pressed: {
                    backgroundColor: semanticColors[SemanticColor.FillRowPressed],
                },
            }}
        >
            {options.imageName && (
                <VisualView
                    version={widget.version}
                    parentStyleNames={parentStyleNames}
                    styleName="infoBannerIcon"
                    iconName={options.imageName}
                    iconHierarchy="primary"
                    iconVariant="circle"
                    isDisabled={isDisabled}
                    opacity={isDisabled ? 0.4 : 1}
                />
            )}
            <CoreLayoutView
                parentStyleNames={parentStyleNames}
                styleName="infoBannerVerticalLayout"
                styleProps={{
                    orientation: ORIENTATION_ENUM.VERTICAL,
                    lineX: 'start',
                    width: '100%',
                    gap: 4,
                }}
            >
                {messages.title && (
                    <CoreTextView
                        parentStyleNames={parentStyleNames}
                        styleName="infoBannerTitle"
                        isDisabled={isDisabled}
                        text={messages.title}
                        styleProps={{
                            fontStyle: { fontSize: 17, lineHeight: 24 },
                            main: semanticColors[SemanticColor.TextPrimary],
                            maxLines: 2,
                            disabled: {
                                main: semanticColors[SemanticColor.TextPrimaryDisabled],
                            },
                        }}
                    />
                )}
                {messages.subtitle && (
                    <CoreTextView
                        parentStyleNames={parentStyleNames}
                        styleName="infoBannerSubtitle"
                        isDisabled={isDisabled}
                        text={messages.subtitle}
                        styleProps={{
                            fontStyle: { fontSize: 14, lineHeight: 20 },
                            main: semanticColors[SemanticColor.TextSecondary],
                            maxLines: 2,
                            disabled: {
                                main: semanticColors[SemanticColor.TextPrimaryDisabled],
                            },
                        }}
                    />
                )}
            </CoreLayoutView>
        </CoreLayoutView>
    );
};
